import {
  Badge,
  Button,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  Link,
  OverlayDrawer,
  Spinner,
  Text,
  Tooltip,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  Dismiss24Regular,
  Dismiss16Regular,
  AlertOff24Regular,
  Checkmark16Regular,
  ArrowClockwise16Regular,
} from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { formatDateToNow } from '@moko/utils';
import type { INotification } from './types';

export interface NotificationsDrawerProps {
  open: boolean;
  onClose: () => void;
  notifications: INotification[];
  unreadCount: number;
  loading: boolean;
  loadingMore: boolean;
  failed: boolean;
  hasMore: boolean;
  onItemClick: (notificationId: string) => void;
  onDismiss: (notificationId: string) => void;
  onMarkAllRead: () => void;
  onLoadMore: () => void;
  onRetry: () => void;
  resolveEntityHref?: (
    entityType: string,
    entityId: number
  ) => string | undefined;
  onNavigate?: (href: string) => void;
  onCloseAfterNavigate?: () => void;
}

const useStyles = makeStyles({
  headerTitle: {
    display: 'inline-flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalS,
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: tokens.spacingVerticalS,
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalXXL,
  },
  item: {
    position: 'relative',
    display: 'flex',
    alignItems: 'flex-start',
    columnGap: tokens.spacingHorizontalM,
    padding: tokens.spacingVerticalM,
    paddingRight: tokens.spacingHorizontalXXL,
    borderRadius: tokens.borderRadiusLarge,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    cursor: 'pointer',
    backgroundColor: tokens.colorNeutralBackground1,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  itemUnread: {
    backgroundColor: tokens.colorBrandBackground2,
    border: `1px solid ${tokens.colorBrandStroke1}`,
  },
  dot: {
    flexShrink: 0,
    width: '8px',
    height: '8px',
    borderRadius: tokens.borderRadiusCircular,
    marginTop: '6px',
    backgroundColor: tokens.colorBrandForeground1,
  },
  dotPlaceholder: {
    flexShrink: 0,
    width: '8px',
    marginTop: '6px',
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '2px',
    flex: 1,
    minWidth: 0,
  },
  title: {
    fontWeight: tokens.fontWeightSemibold,
  },
  titleRead: {
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground2,
  },
  message: {
    color: tokens.colorNeutralForeground2,
    lineHeight: tokens.lineHeightBase200,
  },
  time: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  metaRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    columnGap: tokens.spacingHorizontalS,
    marginTop: '2px',
  },
  goTo: {
    fontSize: tokens.fontSizeBase200,
  },
  dismiss: {
    position: 'absolute',
    top: '6px',
    right: '6px',
  },
  centered: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    rowGap: tokens.spacingVerticalM,
    padding: '48px 16px',
    color: tokens.colorNeutralForeground3,
  },
  loadMore: {
    display: 'flex',
    justifyContent: 'center',
    padding: tokens.spacingVerticalM,
  },
});

const isUnread = (n: INotification) => !n.readAt && !n.dismissedAt;

/**
 * Presentational notification list drawer. Theme-agnostic: renders no
 * FluentProvider of its own (NotificationsBell supplies one so the portalled
 * content keeps the host theme).
 */
const NotificationsDrawer = ({
  open,
  onClose,
  notifications,
  unreadCount,
  loading,
  loadingMore,
  failed,
  hasMore,
  onItemClick,
  onDismiss,
  onMarkAllRead,
  onLoadMore,
  onRetry,
  resolveEntityHref,
  onNavigate,
  onCloseAfterNavigate,
}: NotificationsDrawerProps) => {
  const { t } = useTranslation();
  const styles = useStyles();

  const renderContent = () => {
    if (loading) {
      return (
        <div className={styles.centered}>
          <Spinner size="small" label={t('loading', 'Loading...')} />
        </div>
      );
    }
    if (failed) {
      return (
        <div className={styles.centered}>
          <Text size={300} style={{ color: tokens.colorNeutralForeground3 }}>
            {t('notificationsFailed', 'Could not load notifications')}
          </Text>
          <Button appearance="secondary" size="small" onClick={onRetry}>
            {t('retry', 'Retry')}
          </Button>
        </div>
      );
    }
    if (notifications.length === 0) {
      return (
        <div className={styles.centered}>
          <AlertOff24Regular />
          <Text size={300} style={{ color: tokens.colorNeutralForeground3 }}>
            {t('noNotifications', 'You are all caught up')}
          </Text>
        </div>
      );
    }
    return (
      <div className={styles.list}>
        {notifications.map((notif) => {
          const unread = isUnread(notif);
          const entityHref =
            resolveEntityHref && notif.entityType && notif.entityId
              ? resolveEntityHref(notif.entityType, notif.entityId)
              : undefined;
          return (
            <div
              key={notif.notificationId}
              className={`${styles.item} ${unread ? styles.itemUnread : ''}`}
              role="button"
              tabIndex={0}
              onClick={() => unread && onItemClick(notif.notificationId)}
              onKeyDown={(e) => {
                if ((e.key === 'Enter' || e.key === ' ') && unread) {
                  e.preventDefault();
                  onItemClick(notif.notificationId);
                }
              }}
            >
              <span className={unread ? styles.dot : styles.dotPlaceholder} />
              <div className={styles.body}>
                <Text
                  size={300}
                  className={unread ? styles.title : styles.titleRead}
                >
                  {notif.title}
                </Text>
                {notif.body && (
                  <Text size={200} className={styles.message}>
                    {notif.body}
                  </Text>
                )}
                <div className={styles.metaRow}>
                  {entityHref ? (
                    <Link
                      className={styles.goTo}
                      href={entityHref}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (unread) onItemClick(notif.notificationId);
                        if (onNavigate) {
                          e.preventDefault();
                          onNavigate(entityHref);
                          onCloseAfterNavigate?.();
                        }
                      }}
                    >
                      {t('goToEntity', 'Go to {{entity}}', {
                        entity: notif.entityType,
                      })}
                    </Link>
                  ) : (
                    <span />
                  )}
                  <span className={styles.time}>
                    {formatDateToNow(notif.createdAt)}
                  </span>
                </div>
              </div>
              <Tooltip content={t('dismiss', 'Dismiss')} relationship="label">
                <Button
                  className={styles.dismiss}
                  appearance="subtle"
                  size="small"
                  icon={<Dismiss16Regular />}
                  aria-label={t('dismiss', 'Dismiss')}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDismiss(notif.notificationId);
                  }}
                />
              </Tooltip>
            </div>
          );
        })}
        {hasMore && (
          <div className={styles.loadMore}>
            {loadingMore ? (
              <Spinner size="tiny" />
            ) : (
              <Button appearance="subtle" size="small" onClick={onLoadMore}>
                {t('loadMore', 'Load more')}
              </Button>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <OverlayDrawer
      position="end"
      open={open}
      onOpenChange={(_, data) => {
        if (!data.open) onClose();
      }}
      size="small"
    >
      <DrawerHeader>
        <DrawerHeaderTitle
          action={
            <Button
              appearance="subtle"
              aria-label={t('close', 'Close')}
              icon={<Dismiss24Regular />}
              onClick={onClose}
            />
          }
        >
          <span className={styles.headerTitle}>
            {t('notifications', 'Notifications')}
            {unreadCount > 0 && (
              <Badge appearance="filled" color="danger" size="small">
                {unreadCount}
              </Badge>
            )}
          </span>
        </DrawerHeaderTitle>
      </DrawerHeader>
      <DrawerBody>
        <div className={styles.toolbar}>
          <Button
            appearance="subtle"
            size="small"
            icon={<ArrowClockwise16Regular />}
            disabled={loading}
            onClick={onRetry}
          >
            {t('refresh', 'Refresh')}
          </Button>
          <Button
            appearance="subtle"
            size="small"
            icon={<Checkmark16Regular />}
            disabled={unreadCount === 0}
            onClick={onMarkAllRead}
          >
            {t('markAllRead', 'Mark all read')}
          </Button>
        </div>
        {renderContent()}
      </DrawerBody>
    </OverlayDrawer>
  );
};

export default NotificationsDrawer;
